// DLG-133 User Access (legacy pages/shared/_partials/UserAccess.vue; field note backoffice.accounts.detail): for Radish
// staff and resellers, one user's access in the account on screen. "User Access: {name}", then **Accounts** (purple
// count) with Account Name and Go to Account, and **Locations** (orange count) with Location Name, Role, and Log in,
// Go to Location, Manage Role and Remove User; Close. Read from `identity.userAccess` in the account.
//
// Differences from legacy, each a fixed bug: every location row names the role the user holds there (legacy showed one
// role, the user's role at the current location, on every row: B194); Manage Role and Remove User are offered only on a
// location's own user, the one a location can re-role or remove (legacy offered Manage Role on account-wide rows too);
// the account row's Remove User ends the account-wide membership (identity.removeAccountUser), where legacy's button
// confirmed and then did nothing (B193; drawn since B1279, once account-wide removal existed); and
// the remove confirmation names the user and location (legacy asked to "delete this Location": B195).
// Not here yet: the user's other accounts (legacy listed every account of the user; a platform-scope read is needed).
import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, LoadingState, SECTION_HEADING, useToast, TableActionButton, GoToIcon, LoginIcon, RemoveAccessIcon } from '@radish/ui-web';
import { useApi, useCommand, useLocationQuery } from '../../data/api.tsx';
import { MATERIAL_MANAGE_ACCOUNTS, MaterialIcon } from '../../shell/material-icons.tsx';
import { LoadError } from '../../shell/page.tsx';
import { useMembers } from '../users/data.ts';
import { ChangeRoleDialog, ConfirmDialog } from '../users/UsersPage.tsx';

const USER_ACCESS = 'identity.userAccess';
const ACCOUNT_MEMBERS = 'identity.accountMembers.list';
// Legacy UserAccess.vue's q-avatars and Super Admin badge are Quasar `purple` / `orange`, which legacy's brand turns into
// the account page's purple-button #ad48e0 / orange-button #f79206 (pale #c59afe / #ffd497 in dark; the AA light theme
// darkens them to #a846d9 / #b45f00 for the white numbers, as it does the Invite buttons). Legacy keeps their
// numbers white in dark, near-invisible on the pale fills (legacy bug B1347); Next uses count-ink as the account page does.
const PURPLE = 'bg-purple-button';
const ORANGE = 'bg-orange-button';

type Place = { readonly locationId: string; readonly name: string; readonly slug: string; readonly roleName: string; readonly direct: boolean };

/** Legacy UserAccess.vue's "User Access: {name}": text-subtitle1 bold at 1.15rem (18.4 px on 28) (B1345). */
const ACCESS_TITLE = 'text-[1.15rem] leading-7 font-bold tracking-[0.00937em] text-text-1';

/**
 * Legacy's `q-mt-md flex items-center` row: a UiSectionHeading in text-subtitle1 (its title 12 px down, its empty
 * subheading's q-mt-xs 4 px under it, so the block is 37.6 tall) and the 22 px count q-avatar in a `tw-mb-1` box,
 * centred beside it. The table starts at the row's foot (B1345).
 */
function Heading({ title, count, tone }: { readonly title: string; readonly count: number; readonly tone: string }) {
  return (
    <div className="mt-4 flex items-center">
      <h4 className={`mr-2 pt-3 pb-1 ${SECTION_HEADING}`}>{title}</h4>
      <div className="mb-1">
        <span className={`flex size-[22px] items-center justify-center rounded-full text-[11px] font-semibold text-count-ink ${tone}`}>{count}</span>
      </div>
    </div>
  );
}

/**
 * Legacy's q-badge (12 px on 12, weight 400, 2 px by 6 px, radius 4): purple for Super Admin, the primary colour for a
 * custom role. Legacy keeps the text white in dark on both pale fills (#c59afe, and primary's #b4d5ac at about 1.6:1:
 * legacy bug B1347); Next inks them as the counts and the primary buttons do.
 */
function RoleBadge({ name }: { readonly name: string }) {
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[12px] leading-3 font-normal ${name === 'Super Admin' ? `${PURPLE} text-count-ink` : 'bg-primary text-white dark:text-text-inverted'}`}>{name}</span>;
}

/**
 * Legacy's q-markup-table (flat): a scrolling container round a table whose cells never wrap; 44 px header cells in 12 px
 * 500 text-1 on #f4f4f4 (none in dark) between line rules; body cells 13 px with Quasar's 7 px by 16 px padding and the
 * 12 % row rule, the last row's rule the line colour; separate borders, as DataTable's (B1226), so a row is 50 (B1346).
 */
const ACCESS_TABLE = 'w-full border-separate border-spacing-0 text-[13px]';
const ACCESS_TH = 'h-11 border-y border-line bg-gray-alt dark:bg-transparent px-4 py-[7px] text-[12px] font-medium whitespace-nowrap text-text-1';
const ACCESS_TD = 'border-b border-black/[0.12] [tr:last-child_&]:border-line px-4 py-[7px] whitespace-nowrap text-text-1 dark:border-line dark:text-text-2';

/** Legacy's desktop row hover (Quasar `tbody tr:hover > td:before`, 6 % black in light), as DataTable's `rowHover`. */
const ROW_HOVER = "relative before:pointer-events-none before:absolute before:inset-0 before:content-[''] group-hover/row:before:bg-row-hover";

/**
 * The body of legacy UserAccess.vue, shared by the account pages' dialog and the staff Users page's (one legacy
 * component, so one here): the title, Accounts and Locations with their counts, and each row's actions from the caller.
 */
export function UserAccessBody<A extends { readonly accountId: string; readonly name: string }, L extends { readonly locationId: string; readonly name: string; readonly roleName: string }>({ name, accounts, locations, accountActions, locationActions }: {
  readonly name: string;
  readonly accounts: readonly A[];
  readonly locations: readonly L[];
  readonly accountActions: (account: A) => ReactNode;
  readonly locationActions: (location: L) => ReactNode;
}) {
  return (
    <div data-testid="user-access" className="mb-4">
      <p className={ACCESS_TITLE}>User Access: {name}</p>
      <Heading title="Accounts" count={accounts.length} tone={PURPLE} />
      <div className="overflow-x-auto">
        <table className={ACCESS_TABLE} aria-label="Accounts">
          <thead><tr><th className={`${ACCESS_TH} text-left`}>Account Name</th><th className={ACCESS_TH}><span className="sr-only">Actions</span></th></tr></thead>
          <tbody className="text-text-1 dark:text-text-2">
            {accounts.map((a) => (
              <tr key={a.accountId} className="group/row">
                <td className={`${ACCESS_TD} ${ROW_HOVER}`}>{a.name}</td>
                <td className={`${ACCESS_TD} ${ROW_HOVER} text-right *:align-middle`}>{accountActions(a)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Heading title="Locations" count={locations.length} tone={ORANGE} />
      <div className="overflow-x-auto">
        <table className={ACCESS_TABLE} aria-label="Locations">
          <thead><tr><th className={`${ACCESS_TH} text-left`}>Location Name</th><th className={`${ACCESS_TH} text-center`}>Role</th><th className={ACCESS_TH}><span className="sr-only">Actions</span></th></tr></thead>
          <tbody className="text-text-1 dark:text-text-2">
            {locations.map((l) => (
              <tr key={l.locationId} className="group/row" data-testid="user-access-location">
                <td className={`${ACCESS_TD} ${ROW_HOVER}`}>{l.name}</td>
                <td className={`${ACCESS_TD} ${ROW_HOVER} text-center`}>{l.roleName === '-' ? '-' : <RoleBadge name={l.roleName} />}</td>
                <td className={`${ACCESS_TD} ${ROW_HOVER} text-right *:align-middle`}>{locationActions(l)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Manage Role on a location's own user: the location's Change Role dialog over its member row. */
function ManageRole({ locationId, userId, onClose }: { readonly locationId: string; readonly userId: string; readonly onClose: () => void }) {
  const members = useMembers(locationId);
  const member = members.data?.members.find((m) => m.userId === userId && m.scope === 'location') ?? null;
  return <ChangeRoleDialog locationId={locationId} member={member} onClose={onClose} />;
}

export function UserAccessDialog({ accountId, userId, onClose, onLogIn }: {
  readonly accountId: string;
  /** The user whose access is shown; null keeps the dialog closed. */
  readonly userId: string | null;
  readonly onClose: () => void;
  readonly onLogIn: (locationId: string, slug: string) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { environment } = useApi();
  const access = useLocationQuery(accountId, USER_ACCESS, { userId }, (c) => c.queries.identity.userAccess({ userId: userId as never }), userId !== null);
  const remove = useCommand(accountId, (c, place: Place, call) => c.commands.identity.removeMember({ userId: userId as never, locationId: place.locationId as never }, call), [USER_ACCESS]);
  const [managing, setManaging] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Place | null>(null);
  // B1279: legacy UserAccess.vue's accounts rows carry Remove User (RemoveAccessIcon) beside Go to Account.
  const removeAccount = useCommand(accountId, (c, account: { readonly accountId: string }, call) => c.commands.identity.removeAccountUser({ accountId: account.accountId as never, userId: userId as never }, call), [ACCOUNT_MEMBERS]);
  const [removingAccount, setRemovingAccount] = useState<{ readonly accountId: string; readonly name: string } | null>(null);
  const data = access.data;

  const go = async (to: string) => {
    onClose();
    await navigate({ to: to as '/accounts' });
  };
  const refresh = async (locationId: string) => {
    await queryClient.invalidateQueries({ queryKey: [environment.profile, accountId, USER_ACCESS] });
    await queryClient.invalidateQueries({ queryKey: [environment.profile, locationId, 'identity.members.list'] });
  };

  return (
    <>
      <Dialog
        open={userId !== null && managing === null && removing === null && removingAccount === null}
        onOpenChange={(o) => { if (!o) onClose(); }}
        title="User Access"
        phoneFooterFill
        testId="user-access-dialog"
        footer={<Button onClick={onClose}>Close</Button>}
      >
        {access.isPending ? <LoadingState /> : access.isLoadingError ? <LoadError error={access.error} /> : data === undefined ? null : (
          <UserAccessBody
            name={data.name}
            accounts={data.accounts}
            locations={data.locations}
            accountActions={(a) => (
              <span className="inline-flex">
                <TableActionButton label="Go to Account" onClick={() => void go(`/accounts/${a.accountId}`)}><GoToIcon /></TableActionButton>
                <TableActionButton label={`Remove User from ${a.name}`} title="Remove User" onClick={() => setRemovingAccount(a)}><RemoveAccessIcon /></TableActionButton>
              </span>
            )}
            locationActions={(l) => (
              <>
                <TableActionButton label={`Log in to ${l.name}`} onClick={() => { onClose(); onLogIn(l.locationId, l.slug); }}><LoginIcon /></TableActionButton>
                <TableActionButton label={`Go to ${l.name}`} onClick={() => void go(`/accounts/${accountId}/location/${l.locationId}`)}><GoToIcon /></TableActionButton>
                {l.direct ? (
                  <>
                    <TableActionButton label={`Manage Role at ${l.name}`} onClick={() => setManaging(l.locationId)}>{/* Legacy UserAccess.vue: q-icon name="manage_accounts" color="primary" size 20 (B1559). */}<MaterialIcon path={MATERIAL_MANAGE_ACCOUNTS} size={20} className="!size-5 text-primary" testId="manage-role-icon" /></TableActionButton>
                    <TableActionButton label={`Remove User from ${l.name}`} onClick={() => setRemoving(l)}><RemoveAccessIcon /></TableActionButton>
                  </>
                ) : null}
              </>
            )}
          />
        )}
      </Dialog>
      {managing === null || userId === null ? null : (
        <ManageRole locationId={managing} userId={userId} onClose={() => { const at = managing; setManaging(null); void refresh(at); }} />
      )}
      <ConfirmDialog
        open={removingAccount !== null}
        title="Remove User"
        message={`Are you sure you want to remove ${data?.name ?? ''} from ${removingAccount?.name ?? ''}?`}
        action="Remove"
        busy={removeAccount.isPending}
        onClose={() => setRemovingAccount(null)}
        onConfirm={() => {
          if (removingAccount === null) return;
          // Legacy's accessRemoved hands back to the account's users list: the dialog closes on success rather than reload
          // this user's access, which an account-wide-only user no longer has (identity.member_not_found).
          removeAccount.mutate(removingAccount, {
            onSuccess: () => {
              setRemovingAccount(null);
              queryClient.removeQueries({ queryKey: [environment.profile, accountId, USER_ACCESS] });
              onClose();
              toast.success('User removed successfully from account');
            },
            onError: (e) => toast.error(e.message),
          });
        }}
      />
      <ConfirmDialog
        open={removing !== null}
        title="Remove User"
        message={`Are you sure you want to remove ${data?.name ?? ''} from ${removing?.name ?? ''}?`}
        action="Remove"
        busy={remove.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing === null) return;
          const at = removing.locationId;
          remove.mutate(removing, { onSuccess: () => { setRemoving(null); toast.success('User removed'); void refresh(at); }, onError: (e) => toast.error(e.message) });
        }}
      />
    </>
  );
}
